// chatter.ts ↔ game/chatter.{h,cpp} + game/chatcontroller.h — the chat
// plumbing every MP lobby/room/game form shares: Chatter mediates between the
// platform chat surface (IChatController — the WEB build's DOM overlay,
// game/sdl/web/webchatcontroller.cpp; tests use MemoryChatController) and the
// transport (gptra->SendChat).
//
// The C++ sigslot signals (SignalOnBlink/SignalOnPlayers) become plain
// callback lists with connect/disconnect — same single-listener-per-form use.

import type { SoundMgr } from './shellform.ts';
import { ksfxGuiCheckBoxTap } from './uikit.ts';
import { TimeoutTimer, type ITimeout, type TimerMgr } from './timer.ts';

// game/chatcontroller.h:6-11
export interface IChatControllerCallback {
  OnChatDismissed(): void;
  OnChatSend(chat: string): void;
  OnPlayers(): void;
}

// game/chatcontroller.h:13-22
export interface IChatController {
  Clear(): void;
  AddChat(player: string, chat: string): void;
  Show(fShow: boolean): void;
  SetTitle(title: string): void;
  GetTitle(): string;
  SetCallback(pcccb: IChatControllerCallback | null): IChatControllerCallback | null;
}

/** A state-only controller (the test/headless default — the production DOM
 *  overlay is the embedder's, mirroring webchatcontroller.cpp). */
export class MemoryChatController implements IChatController {
  lines: { player: string; chat: string }[] = [];
  visible = false;
  title = 'Chat';
  private cb: IChatControllerCallback | null = null;

  Clear(): void {
    this.lines = [];
  }
  AddChat(player: string, chat: string): void {
    this.lines.push({ player, chat });
  }
  Show(fShow: boolean): void {
    const was = this.visible;
    this.visible = fShow;
    // webchatcontroller.cpp onHide → Chat_OnDone → OnChatDismissed
    if (was && !fShow) this.cb?.OnChatDismissed();
  }
  SetTitle(title: string): void {
    this.title = title;
  }
  GetTitle(): string {
    return this.title;
  }
  SetCallback(pcccb: IChatControllerCallback | null): IChatControllerCallback | null {
    const old = this.cb;
    this.cb = pcccb;
    return old;
  }
  /** Test reach: the user typed + sent a chat line (the DOM send button). */
  userSend(chat: string): void {
    this.cb?.OnChatSend(chat);
  }
  /** Test reach: the user pressed the PLAYERS button. */
  userPlayers(): void {
    this.cb?.OnPlayers();
  }
  /** Test reach: the user dismissed the chat overlay (DONE button). */
  userDismiss(): void {
    if (this.visible) {
      this.visible = false;
      this.cb?.OnChatDismissed();
    }
  }
}

/** The C++ globals Chatter reaches (gsndm, gtimm, gptra->SendChat,
 *  HostGetChatController, the LoginHandler name). */
export interface ChatterDeps {
  chatc: IChatController; // HostGetChatController()
  sndm: SoundMgr; // gsndm
  gtimm: TimerMgr; // gtimm (the blinker)
  getPlayerName(): string; // handler_.GetPlayerName
  sendChat(chat: string): void; // gptra->SendChat
}

// game/chatter.cpp
export class Chatter implements IChatControllerCallback, ITimeout {
  // base::signal1<bool> SignalOnBlink / signal0<> SignalOnPlayers
  private readonly onBlink: ((fOn: boolean) => void)[] = [];
  private readonly onPlayers: (() => void)[] = [];

  private readonly deps: ChatterDeps;
  private readonly chatc_: IChatController;
  private readonly old_: IChatControllerCallback | null;
  private readonly blinker_: TimeoutTimer;
  private title_ = '';
  private chatting_ = false;
  private on_ = true;

  // chatter.cpp:5-10
  constructor(deps: ChatterDeps) {
    this.deps = deps;
    this.blinker_ = new TimeoutTimer(deps.gtimm);
    this.chatc_ = deps.chatc;
    this.old_ = this.chatc_.SetCallback(this);
    this.ClearChat();
  }

  // chatter.cpp:12-15 — ~Chatter
  Dispose(): void {
    this.chatc_.SetCallback(this.old_);
    this.chatc_.Show(false);
    this.blinker_.Stop();
  }

  // the sigslot connect/disconnect surface (single listener per form)
  connectBlink(fn: (fOn: boolean) => void): void {
    this.onBlink.push(fn);
  }
  disconnectBlink(fn: (fOn: boolean) => void): void {
    const i = this.onBlink.indexOf(fn);
    if (i >= 0) this.onBlink.splice(i, 1);
  }
  connectPlayers(fn: () => void): void {
    this.onPlayers.push(fn);
  }
  disconnectPlayers(fn: () => void): void {
    const i = this.onPlayers.indexOf(fn);
    if (i >= 0) this.onPlayers.splice(i, 1);
  }

  // chatter.cpp:17-35
  AddChat(player: string | null, chat: string, system: boolean): void {
    const psz = player ?? '';
    // Received chat from server, add it to the chat window
    if (system) {
      this.chatc_.AddChat('', chat);
    } else {
      this.chatc_.AddChat(psz, chat);
      if (!this.chatting_) {
        const name = this.deps.getPlayerName();
        if (name !== psz) {
          // Blink for player chats but not server messages
          if (psz !== '') {
            this.deps.sndm.PlaySfx(ksfxGuiCheckBoxTap);
            this.StartBlinking();
          }
        }
      }
    }
  }

  // chatter.cpp:37-40
  ClearChat(): void {
    this.chatc_.Clear();
    this.StopBlinking();
  }

  // chatter.cpp:42-48
  ShowChat(): void {
    if (!this.chatting_) {
      this.chatc_.Show(true);
      this.chatting_ = true;
      this.StopBlinking();
    }
  }

  // chatter.cpp:50-57
  SetChatTitle(title: string): void {
    if (title !== this.title_) {
      this.title_ = title;
      this.ClearChat();
      this.StopBlinking();
    }
    // The DISTINCT logical title ('Room Chat' / 'Game Chat') drives the clear-on-change
    // above (entering staging clears the room log), but the surface always DISPLAYS a
    // short "Chat" — the full label was truncated in the overlay header between the
    // PLAYERS and DONE buttons on phone-width screens ("GAME CHA…").
    this.chatc_.SetTitle('Chat');
  }

  // chatter.cpp:59-64
  HideChat(): void {
    if (this.chatting_) {
      this.chatc_.Show(false);
      this.chatting_ = false;
    }
  }

  /** True while the chat surface is open (chatting_). Read-only query — lets a DOM
   *  embedder reflect the CHAT-button unread badge (a chat that arrives while open is
   *  not "unread") without duplicating the open/closed bookkeeping the Chatter owns. */
  IsChatting(): boolean {
    return this.chatting_;
  }

  // chatter.cpp:66-70 (IChatControllerCallback)
  OnChatDismissed(): void {
    // Chat window was dismissed; remember this so next time chat comes in
    // the blinker will start.
    this.chatting_ = false;
  }

  // chatter.cpp:72-77
  OnChatSend(chat: string): void {
    // User pressed the Send button; send chat to the server
    this.deps.sendChat(chat);
  }

  // chatter.cpp:79-81
  OnPlayers(): void {
    for (const fn of this.onPlayers.slice()) fn();
  }

  // chatter.cpp:83-85 (ITimeout)
  OnTimeout(_id: number): void {
    this.ShowChatButton(!this.on_);
  }

  // chatter.cpp:87-92
  private ShowChatButton(on: boolean): void {
    if (on !== this.on_) {
      this.on_ = on;
      for (const fn of this.onBlink.slice()) fn(on);
    }
  }

  // chatter.cpp:94-96
  private StartBlinking(): void {
    this.blinker_.Start(this, 1000, 0, false);
  }

  // chatter.cpp:98-101
  private StopBlinking(): void {
    this.blinker_.Stop();
    this.ShowChatButton(true);
  }
}
